import React, { useState, useEffect } from 'react';
import { type UserProfile } from '../types';
import { supabase } from '../utils/supabase';
import { Lock, Bell, Clock, Smartphone, Send, Globe } from 'lucide-react';
import { isRunningInIOSApp, requestNotificationPermission, setupIOSBridge } from '../utils/iosBridge';
import { sendPushNotification } from '../utils/pushNotifications';
import { useTranslation } from '../utils/translations';

interface SettingsProps {
  user?: UserProfile;
  onLanguageSelect?: () => void;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface NotificationSettings {
  daily_reminder: boolean;
  weekly_progress: boolean;
  new_lessons: boolean;
  streak_alerts: boolean;
  reminder_time: string;
}

interface DeviceToken {
  id: string;
  device_name: string;
  device_type: string;
  is_active: boolean;
  created_at: string;
}

export function Settings({ user, onLanguageSelect }: SettingsProps) {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const { t } = useTranslation(user?.nativeLanguage);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    daily_reminder: true,
    weekly_progress: true,
    new_lessons: true,
    streak_alerts: true,
    reminder_time: '09:00'
  });
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationsSaving, setNotificationsSaving] = useState(false);
  const [testingNotification, setTestingNotification] = useState(false);
  const [devices, setDevices] = useState<DeviceToken[]>([]);
  const [isInIOSApp, setIsInIOSApp] = useState(false);
  const [lessonsCompletedCount, setLessonsCompletedCount] = useState(0);

  useEffect(() => {
    setIsInIOSApp(isRunningInIOSApp());

    if (user?.uid) {
      setupIOSBridge(user.uid);
      loadNotificationSettings();
      loadDevices();
      loadLessonsCompleted();
    }
  }, [user?.uid]);

  const loadLessonsCompleted = async () => {
    if (!user?.uid) return;

    try {
      const tableName = user.nativeLanguage === 'Persian' ? 'persian_lesson_progress' : 'lesson_progress';

      const { count, error } = await supabase
        .from(tableName)
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.uid)
        .eq('completed', true);

      if (error) throw error;
      setLessonsCompletedCount(count || 0);
    } catch (err) {
      console.error('Error loading lessons completed:', err);
    }
  };

  const loadNotificationSettings = async () => {
    if (!user?.uid) return;

    setNotificationsLoading(true);
    try {
      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('user_id', user.uid)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setNotificationSettings({
          daily_reminder: data.daily_reminder,
          weekly_progress: data.weekly_progress,
          new_lessons: data.new_lessons,
          streak_alerts: data.streak_alerts,
          reminder_time: data.reminder_time?.slice(0, 5) || '09:00'
        });
      }
    } catch (err) {
      console.error('Error loading notification settings:', err);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const loadDevices = async () => {
    if (!user?.uid) return;

    try {
      const { data, error } = await supabase
        .from('device_tokens')
        .select('id, device_name, device_type, is_active, created_at')
        .eq('user_id', user.uid)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDevices(data || []);
    } catch (err) {
      console.error('Error loading devices:', err);
    }
  };

  const saveNotificationSettings = async (newSettings: NotificationSettings) => {
    if (!user?.uid) return;

    setNotificationsSaving(true);
    try {
      const { error } = await supabase
        .from('notification_settings')
        .upsert({
          user_id: user.uid,
          daily_reminder: newSettings.daily_reminder,
          weekly_progress: newSettings.weekly_progress,
          new_lessons: newSettings.new_lessons,
          streak_alerts: newSettings.streak_alerts,
          reminder_time: newSettings.reminder_time + ':00',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
      setSuccess('Notification settings saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving notification settings:', err);
      setError('Failed to save notification settings');
    } finally {
      setNotificationsSaving(false);
    }
  };

  const handleNotificationToggle = (key: keyof NotificationSettings) => {
    if (key === 'reminder_time') return;

    const newSettings = {
      ...notificationSettings,
      [key]: !notificationSettings[key]
    };
    setNotificationSettings(newSettings);
    saveNotificationSettings(newSettings);
  };

  const handleReminderTimeChange = (time: string) => {
    const newSettings = {
      ...notificationSettings,
      reminder_time: time
    };
    setNotificationSettings(newSettings);
    saveNotificationSettings(newSettings);
  };

  const handleTestNotification = async () => {
    if (!user?.uid) return;

    setTestingNotification(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await sendPushNotification(
        user.uid,
        'Test Notification',
        'This is a test notification to verify your setup!'
      );

      console.log('Push notification result:', result);

      if (!result) {
        setError('Failed to send - no response from server');
        return;
      }

      if (result.error) {
        setError(result.error);
        return;
      }

      if (result.sent === 0) {
        setError('No registered devices found. Make sure you have enabled notifications in the iOS app.');
        return;
      }

      if (result.success) {
        setSuccess(`Test notification sent to ${result.sent} device(s)!`);
      } else if (result.failures && result.failures.length > 0) {
        const firstError = result.failures[0];
        setError(`Notification failed: ${firstError.error || 'Unknown error'}${firstError.details ? ` - ${firstError.details}` : ''}`);
      }
    } catch (err) {
      console.error('Error sending test notification:', err);
      setError('Failed to send test notification: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setTestingNotification(false);
      setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 8000);
    }
  };

  const handleEnableNotifications = () => {
    requestNotificationPermission();
  };

  const removeDevice = async (deviceId: string) => {
    try {
      const { error } = await supabase
        .from('device_tokens')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', deviceId);

      if (error) throw error;

      setDevices(devices.filter(d => d.id !== deviceId));
      setSuccess('Device removed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error removing device:', err);
      setError('Failed to remove device');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user!.email,
        password: formData.currentPassword,
      });

      if (signInError) {
        setError('Current password is incorrect');
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (updateError) throw updateError;

      setSuccess('Password updated successfully');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setIsChangingPassword(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password');
    }
  };

  return (
    <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">{t('Account Settings')}</h2>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-md">
          {success}
        </div>
      )}

      <div className="space-y-6">
        {user && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">{t('Account Information')}</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('Email')}</label>
              <p className="mt-1 text-lg">{user.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('Target Language')}</label>
              <p className="mt-1 text-lg">{user.targetLanguage}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('Native Language')}</label>
              <div className="mt-1 flex items-center gap-3">
                <p className="text-lg">{user.nativeLanguage}</p>
                <button
                  onClick={onLanguageSelect}
                  className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-500 border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
                >
                  <Globe className="h-4 w-4 mr-1" />
                  {t('Change')}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('Current Level')}</label>
              <p className="mt-1 text-lg">{user.currentLevel || t('Not set')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('Lessons Completed')}</label>
              <p className="mt-1 text-lg">{lessonsCompletedCount}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t('Member Since')}</label>
              <p className="mt-1 text-lg">{new Date(user.dateJoined).toLocaleDateString()}</p>
            </div>
          </div>
        )}

        <div className="pt-6 border-t">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">{t('Password')}</h3>
            <button
              onClick={() => user && setIsChangingPassword(!isChangingPassword)}
              className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-500"
              disabled={!user}
            >
              <Lock className="h-4 w-4 mr-2" />
              {isChangingPassword ? t('Cancel') : t('Change Password')}
            </button>
          </div>

          {isChangingPassword && (
            <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Update Password
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="pt-6 border-t">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">{t('Push Notifications')}</h3>
          </div>

          {isInIOSApp && devices.length === 0 && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800 mb-3">
                Enable push notifications to receive reminders and updates about your learning progress.
              </p>
              <button
                onClick={handleEnableNotifications}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Bell className="h-4 w-4 mr-2" />
                Enable Notifications
              </button>
            </div>
          )}

          {!isInIOSApp && (
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
              <p className="text-sm text-gray-600">
                {t('Push notifications are available when using the ListenDutch iOS app.')}
              </p>
            </div>
          )}

          {notificationsLoading ? (
            <div className="text-gray-500">Loading notification settings...</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="daily-reminder"
                    type="checkbox"
                    checked={notificationSettings.daily_reminder}
                    onChange={() => handleNotificationToggle('daily_reminder')}
                    disabled={notificationsSaving}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="daily-reminder" className="ml-3 text-sm text-gray-700">
                    {t('Daily practice reminders')}
                  </label>
                </div>
              </div>

              {notificationSettings.daily_reminder && (
                <div className="ml-7 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <label htmlFor="reminder-time" className="text-sm text-gray-600">
                    {t('Remind me at:')}
                  </label>
                  <input
                    type="time"
                    id="reminder-time"
                    value={notificationSettings.reminder_time}
                    onChange={(e) => handleReminderTimeChange(e.target.value)}
                    disabled={notificationsSaving}
                    className="px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  />
                </div>
              )}

              <div className="flex items-center">
                <input
                  id="weekly-progress"
                  type="checkbox"
                  checked={notificationSettings.weekly_progress}
                  onChange={() => handleNotificationToggle('weekly_progress')}
                  disabled={notificationsSaving}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="weekly-progress" className="ml-3 text-sm text-gray-700">
                  {t('Weekly progress updates')}
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="new-lessons"
                  type="checkbox"
                  checked={notificationSettings.new_lessons}
                  onChange={() => handleNotificationToggle('new_lessons')}
                  disabled={notificationsSaving}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="new-lessons" className="ml-3 text-sm text-gray-700">
                  {t('New lesson notifications')}
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="streak-alerts"
                  type="checkbox"
                  checked={notificationSettings.streak_alerts}
                  onChange={() => handleNotificationToggle('streak_alerts')}
                  disabled={notificationsSaving}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="streak-alerts" className="ml-3 text-sm text-gray-700">
                  {t('Streak alerts')}
                </label>
              </div>
            </div>
          )}


          {devices.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                Registered Devices
              </h4>
              <div className="space-y-2">
                {devices.map((device) => (
                  <div
                    key={device.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {device.device_name || 'iPhone'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Added {new Date(device.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => removeDevice(device.id)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
